import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { ModalTitle } from "../../../enums/StringLiterals/modalTitle";
import { SideNavId } from "../../../enums/StringLiterals/sideNavId";
import { SideNavLabel } from "../../../enums/StringLiterals/sideNavLabel";
import { RootState } from "../../../redux/store/store";
import SideNavElement from "../../sideNavElement/sideNavElement";
import HistoryTab from "../../sidePanel/submissionSummarySidePanel/tabs/historyTab";
import CaseSummaryTab, {
  hasCaseSummaryData,
} from "./components/caseSummaryTab";
import PdfRendererTab from "./components/pdfRendererTab";
import ScreenshotRendererTab from "./components/screenshotRendererTab";
import { shouldShowCallingSummaryTab } from "./utils/shouldShowCallingSummaryTab";

interface SftpDocModalProps {
  onClose: () => void;
  /** When true, hides the Calling Summary side tab (e.g. Astera Oncology External). */
  hideCallingSummaryTab?: boolean;
  /**
   * When true, shows Calling Summary only when the selected row has
   * `non_epa_required` of yes or archive (e.g. NYCBS External).
   */
  showCallingSummaryOnlyWhenCallRequired?: boolean;
}

const allTabs = [
  { id: SideNavId.CASE_SUMMARY, label: SideNavLabel.CASE_SUMMARY },
  {
    id: SideNavId.SCREENSHOT_RENDERER,
    label: SideNavLabel.SCREENSHOT_RENDERER,
  },

  { id: SideNavId.PDF_RENDERER, label: SideNavLabel.PDF_RENDERER },
  { id: SideNavId.CALLING_SUMMARY, label: SideNavLabel.CALLING_SUMMARY },
] as const;

/**
 * Full-screen modal for CMM/SFTP order artifacts: case summary (when present),
 * CMM screenshot, calling summary timeline (Firestore calling history), and status letter PDF.
 *
 * @param props - Modal callbacks
 * @param props.onClose - Invoked when the user dismisses the modal
 * @param props.hideCallingSummaryTab - When true, omits the Calling Summary side tab
 * @param props.showCallingSummaryOnlyWhenCallRequired - When true, Calling Summary is shown only for yes/archive non_epa_required
 */
const SftpDocModal = ({
  onClose,
  hideCallingSummaryTab = false,
  showCallingSummaryOnlyWhenCallRequired = false,
}: SftpDocModalProps) => {
  const { metaData } = useSelector((state: RootState) => state.modalSliceNew);
  const rowData = metaData?.selectedRowData;
  const canShowCaseSummary = hasCaseSummaryData(rowData);
  const canShowCallingSummary =
    !hideCallingSummaryTab &&
    (!showCallingSummaryOnlyWhenCallRequired ||
      shouldShowCallingSummaryTab(rowData?.non_epa_required));

  const tabs = useMemo(() => {
    let visibleTabs = canShowCaseSummary
      ? [...allTabs]
      : allTabs.filter((tab) => tab.id !== SideNavId.CASE_SUMMARY);

    if (!canShowCallingSummary) {
      visibleTabs = visibleTabs.filter(
        (tab) => tab.id !== SideNavId.CALLING_SUMMARY,
      );
    }

    return visibleTabs;
  }, [canShowCaseSummary, canShowCallingSummary]);

  const [selectedTab, setSelectedTab] = useState<string>(
    canShowCaseSummary ? SideNavId.CASE_SUMMARY : SideNavId.SCREENSHOT_RENDERER,
  );

  useEffect(() => {
    // If the selected order doesn't have case summary data, don't allow landing on that tab.
    if (!canShowCaseSummary && selectedTab === SideNavId.CASE_SUMMARY) {
      setSelectedTab(SideNavId.SCREENSHOT_RENDERER);
      return;
    }

    if (!canShowCallingSummary && selectedTab === SideNavId.CALLING_SUMMARY) {
      setSelectedTab(SideNavId.SCREENSHOT_RENDERER);
      return;
    }

    // When row changes, if case summary exists, default to it; otherwise default to screenshot renderer.
    if (canShowCaseSummary) {
      setSelectedTab(SideNavId.CASE_SUMMARY);
    } else {
      setSelectedTab(SideNavId.SCREENSHOT_RENDERER);
    }
  }, [canShowCaseSummary, canShowCallingSummary, rowData]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    onClose();
  };

  const renderContent = () => {
    const identifier = rowData?.identifier;
    const responseStatus = rowData?.responseStpStatus?.text;
    const cmmStatus = rowData?.cmmStatus;

    switch (selectedTab) {
      case SideNavId.CASE_SUMMARY:
        return <CaseSummaryTab rowData={rowData} />;
      case SideNavId.SCREENSHOT_RENDERER:
        return (
          <ScreenshotRendererTab orderId={identifier} cmmStatus={cmmStatus} />
        );
      case SideNavId.CALLING_SUMMARY:
        return rowData ? (
          <HistoryTab data={rowData} onClose={handleClose} hideFooter />
        ) : null;
      case SideNavId.PDF_RENDERER:
        return <PdfRendererTab orderId={identifier} status={responseStatus} />;
      // case SideNavId.DENIAL_LETTER:
      //   return <DenialLetterTab orderId={identifier} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      dialogId={ModalId.SFTP_DOC_MODAL}
      title={ModalTitle.SFTP_DOC_MODAL}
      onClose={handleClose}
      onSave={handleSave}
      saveButtonText="Save"
      cancelText="Cancel"
      heightPercentage={90}
      hideFooter={true}
    >
      <div className="flex h-full flex-col">
        <div className="flex flex-1 gap-1 overflow-hidden">
          <div className="side-nav--container flex w-[25%] flex-col overflow-y-auto">
            {tabs.map((tab) => (
              <SideNavElement
                key={tab.id}
                id={tab.id}
                label={tab.label}
                active={tab.id === selectedTab}
                onSelect={(id) => setSelectedTab(id)}
              />
            ))}
          </div>
          <div className="content--container flex h-full min-h-0 w-[75%] flex-1 flex-col overflow-hidden bg-primaryGray-16 pb-3">
            {renderContent()}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SftpDocModal;
